// A Machines card's CPU, Memory and Disk bars as pure data (#6560): the number on the bar,
// how full it is, its tone and the tooltip's sentence. `MachineResourceBar` renders it.
//
// THE TONE IS THE SERVER'S. Each bar reads its `verdict.signals.<kind>` (#6557): the tier
// (`ok` / `warn` / `crit`) and the ratio the tier was read off. Nothing here re-grades a
// raw reading, so the bar and the verdict pill beside it can never disagree. A missing
// reading is "not reported" (`-`), never a zero bar.
//
// THE TOOLTIP STATES ONLY WHAT THE BEAT CARRIES: CPU against the enroll-time processor
// count, memory against the enroll-time total, disk against the workspace total (#6554)
// when the runner reports one, else the free space alone (owner ruling G9). The stop-work
// floor is the machine's own `workspaceDisk.floorBytes`, never a constant.
//
// GB is 1e9 bytes, as everywhere on the Fleet page (`FleetAttention`), so the default
// 5 GiB floor reads "5.4 GB".

import type { UtilBox } from './fleetUtilization';
import { decimal } from './time';

export type ResourceKind = 'cpu' | 'memory' | 'disk';

export const RESOURCE_KINDS: readonly ResourceKind[] = ['cpu', 'memory', 'disk'];

/** `none` is a bar with no reading behind it. */
export type BarTone = 'ok' | 'warn' | 'crit' | 'none';

export type ResourceBar = {
  readonly kind: ResourceKind;
  /** "CPU", "Memory", "Disk". */
  readonly label: string;
  /** The number on the bar: "62%", "100%+", "12.4 GB free", or "-". */
  readonly value: string;
  /** How full the bar is drawn, 0 to 100. */
  readonly fill: number;
  readonly tone: BarTone;
  readonly tip: string;
};

type Tr = (key: string, vars?: Readonly<Record<string, string | number>>) => string;

const GB = 1e9;
const gb = (bytes: number): string => decimal(bytes / GB);

const LABEL: Readonly<Record<ResourceKind, string>> = {
  cpu: 'fleet.machines.bar_cpu',
  memory: 'fleet.machines.bar_memory',
  disk: 'fleet.machines.bar_disk',
};

const TONE: Readonly<Record<string, BarTone>> = {
  ok: 'ok',
  warn: 'warn',
  crit: 'crit',
  not_reported: 'none',
};

const percent = (ratio: number, tr: Tr): string =>
  tr('fleet.machines.percent', { value: Math.round(Math.min(ratio, 1) * 100) });

const fillOf = (ratio: number): number => Math.round(Math.min(Math.max(ratio, 0), 1) * 100);

function cpuBar(box: UtilBox, tr: Tr): Omit<ResourceBar, 'kind' | 'label' | 'tone'> {
  const ratio = box.verdict.signals.cpu.ratio;
  const cores = box.specs?.cpu_cores ?? null;
  if (ratio === null || cores === null) {
    return {
      value: tr('fleet.machines.not_reported'),
      fill: 0,
      tip: tr('fleet.machines.tip_none'),
    };
  }
  if (ratio > 1) {
    return {
      value: tr('fleet.machines.cpu_over'),
      fill: 100,
      tip: tr('fleet.machines.tip_cpu_over', { ratio: decimal(ratio, 2), cores }),
    };
  }
  return {
    value: percent(ratio, tr),
    fill: fillOf(ratio),
    tip: tr('fleet.machines.tip_cpu', { percent: Math.round(ratio * 100), cores }),
  };
}

function memoryBar(box: UtilBox, tr: Tr): Omit<ResourceBar, 'kind' | 'label' | 'tone'> {
  const ratio = box.verdict.signals.memory.ratio;
  const total = box.specs?.mem_bytes ?? null;
  const free = box.stats?.freeMemBytes ?? null;
  if (ratio === null || total === null || free === null) {
    return {
      value: tr('fleet.machines.not_reported'),
      fill: 0,
      tip: tr('fleet.machines.tip_none'),
    };
  }
  return {
    value: percent(ratio, tr),
    fill: fillOf(ratio),
    tip: tr('fleet.machines.tip_memory', { used: gb(total - free), total: gb(total) }),
  };
}

function diskBar(box: UtilBox, tr: Tr): Omit<ResourceBar, 'kind' | 'label' | 'tone'> {
  const ws = box.stats?.workspaceDisk;
  if (ws === undefined || ws.freeBytes === null) {
    return {
      value: tr('fleet.machines.not_reported'),
      fill: 0,
      tip: tr('fleet.machines.tip_none'),
    };
  }
  const floor = gb(ws.floorBytes);
  const free = gb(ws.freeBytes);
  const total = ws.totalBytes ?? null;
  const ratio = box.verdict.signals.disk.ratio;
  if (total === null || total <= 0 || ratio === null) {
    // A runner older than #6554 reports no total: the free space is all there is (G9).
    return {
      value: tr('fleet.machines.disk_free_short', { free }),
      fill: 0,
      tip: tr('fleet.machines.tip_disk_free', { free, floor }),
    };
  }
  return {
    value: percent(ratio, tr),
    fill: fillOf(ratio),
    tip: tr('fleet.machines.tip_disk', {
      used: gb(total - ws.freeBytes),
      total: gb(total),
      free,
      floor,
    }),
  };
}

const BUILD: Readonly<
  Record<ResourceKind, (box: UtilBox, tr: Tr) => Omit<ResourceBar, 'kind' | 'label' | 'tone'>>
> = { cpu: cpuBar, memory: memoryBar, disk: diskBar };

/** One bar of one machine, through the caller's translator (the page's `t`, or a test's `en`). */
export function resourceBar(box: UtilBox, kind: ResourceKind, tr: Tr): ResourceBar {
  return {
    kind,
    label: tr(LABEL[kind]),
    tone: TONE[box.verdict.signals[kind].level] ?? 'none',
    ...BUILD[kind](box, tr),
  };
}

/**
 * Memory in use as a whole percent of the machine, from FREE bytes and the machine's
 * `specs.mem_bytes`: `100 × (total − free) / total`. The move preview reports memory as free
 * bytes before and after (#6768); the drop panel shows it used, as this file's memory bar does.
 */
export function memoryUsedPercent(totalBytes: number, freeBytes: number): number {
  if (totalBytes <= 0) return 0;
  return Math.round((100 * (totalBytes - freeBytes)) / totalBytes);
}
